import * as React from 'react'
import gql from 'graphql-tag'
import Link from 'next/link'
import styled, { css } from 'styled-components'
import { Close } from 'svgIcons'
import { formatDateYY_MM_DD } from 'utils'
import {
  NewsCardItem_NewsItemFragment,
  NotificationScope,
  PageLayoutDocument,
  useRemoveScrap_NewsCardItemMutation,
  GqlUserScrapCountsDocument,
} from 'generated/graphql'
import Avatar from '@app.components/Avatar/Avatar'
import useIsWebview from '@app.modules/hooks/useIsWebview'
import { getWebviewHref } from '@app.modules/util'
import useFlutterInappwebview from 'hooks/useFlutterInappwebview'
import NewsCardItemDeleted from './NewsCardItem.deleted'

gql`
  fragment NewsCardItem_NewsItem on NewsItem {
    id
    viewCount
    createdAt
    deletedAt
    content
    viewerHasBookmarked
    channel {
      id
      url
      name
      owner {
        id
        logoImage {
          id
          url
        }
      }
    }
  }
`

type TProps = {
  data: NewsCardItem_NewsItemFragment
}

const NewsCardItem: React.FC<TProps> = ({ data }) => {
  const isWebview = useIsWebview()
  const { fluterInappwebview } = useFlutterInappwebview()
  const [mutate] = useRemoveScrap_NewsCardItemMutation()

  const handleClickRemoveBtn = () => {
    mutate({
      variables: {
        newsItemId: data.id,
      },
      refetchQueries: [
        {
          query: PageLayoutDocument,
          variables: {
            notificationType: NotificationScope.NOTIFICATION,
          },
        },
        {
          query: GqlUserScrapCountsDocument,
        },
      ],
      onCompleted: () => {
        if (isWebview) {
          fluterInappwebview?.callHandler('reportRemoveScrap')
        }
      },
    })
  }

  if (data.deletedAt !== null)
    return <NewsCardItemDeleted onClick={handleClickRemoveBtn} />
  return (
    <StyledWrapper>
      <div className="lt">
        <Avatar
          type="channel"
          src={data.channel?.owner?.logoImage?.url}
          size={66}
        />
        <div className="content">
          <Link
            href={getWebviewHref(`/channel/${data.channel?.url}`, isWebview)}
          >
            <a className="channel-name">{data.channel?.name}</a>
          </Link>
          <Link
            href={getWebviewHref(
              `/channel/${data.channel?.url}/${data.id}`,
              isWebview,
            )}
          >
            <a className="channel-content">
              {data.content.replace(/<[^>]*>?/g, '')}
            </a>
          </Link>
          <div className="channel-desc">
            <span>{formatDateYY_MM_DD(data.createdAt)}</span>
          </div>
        </div>
      </div>
      <div className="rt">
        <button className="close-btn" onClick={handleClickRemoveBtn}>
          <Close />
        </button>
      </div>
    </StyledWrapper>
  )
}

export default NewsCardItem

const StyledWrapper = styled.div`
  padding: 20px 0;
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid #eeeeee;
  align-items: center;
  > .lt {
    display: flex;
    gap: 28px;
    overflow: hidden;
    .content {
      overflow: hidden;
      .channel-name {
        display: block;
        font-size: 12px;
      }
      .channel-content {
        display: block;
        font-size: 16px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin-bottom: 8px;
      }
      .channel-desc {
        font-size: 12px;
        color: #999;
        span {
          margin-right: 10px;
        }
      }
    }
  }

  .close-btn {
    cursor: pointer;
    background: none;
    padding: 0px;
    border: none;

    svg {
      width: 15px;
      height: 15px;
    }
  }
  > .rt {
    padding: 20px;
  }

  ${({ theme }) =>
    theme.isMobile &&
    css`
      > .lt {
        padding-left: 20px;
        gap: 10px;
      }
    `}
`
